import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { Component, ElementRef, HostListener, Injector, OnInit, ViewChild, forwardRef, computed, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SoCalendarUI } from '../so-calendar/so-calendar.component';
import { DateRangePosition } from './so-date-picker.interfaces';

@Component({
  selector: 'so-date-picker-ui',
  standalone: true,
  templateUrl: './so-date-picker.component.html',
  styleUrls: ['./so-date-picker.component.scss'],
  imports: [CommonModule, A11yModule, SoCalendarUI , ReactiveFormsModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoDatePickerUI),
      multi: true,
    },
  ],
})
export class SoDatePickerUI implements ControlValueAccessor, OnInit {
  selectLabel = input<string>('Fecha');
  selectDisplay = input<'vertical' | 'horizontal'>('horizontal');
  placeholder = input<string>('Seleccionar');
  required = input<boolean>(false);
  overlayTitle = input<string>('Seleccionar fecha');
  triggerOffset = input<number>(8);
  formStyle = input<boolean>(false);
  isFilter = input<boolean>(false);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);
  selectHeight = input<number>(36);

  @ViewChild('overlayElement') overlayElement?: ElementRef<HTMLDivElement>;

  opened = output<void>();
  closed = output<Date | null>();
  valueChange = output<Date | null>();

  private lastTriggerRect: DOMRect | null = null;
  private triggerElement: HTMLElement | null = null;

  public isOpen = signal<boolean>(false);
  public selectDisabled = signal<boolean>(false);
  public position = signal<DateRangePosition | null>(null);

  public selectedDate = signal<Date | null>(null);
  public selectedText = computed(() => {
    const date = this.selectedDate();
    return date ? this.formatter.format(date) : null;
  });
  public selectedValue = computed(() => {
    const date = this.selectedDate();
    return date ? date.toISOString() : null;
  });

  private formatter = new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.controlRequired());

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};
  private touched = false;

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  writeValue(value: Date | string | null): void {
    if (!value) {
      this.setSelection(null);
      return;
    }

    const date = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(date.getTime())) {
      this.setSelection(null);
      return;
    }

    this.setSelection(date);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.selectDisabled.set(isDisabled);
  }

  public onTriggerClick(event: MouseEvent): void {
    if (this.selectDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    this.toggleOverlay(target);
    this.markAsTouched();
  }

  public onTriggerKeyDown(event: KeyboardEvent): void {
    if (this.selectDisabled()) return;
    const triggerKeys = ['Enter', ' ', 'Spacebar'];
    if (!triggerKeys.includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    this.toggleOverlay(target);
    this.markAsTouched();
  }

  public onDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    this.setSelection(date);
    this.emitValue(this.selectedDate());
    this.closeOverlay();
  }

  public onClose(): void {
    if (!this.isOpen()) return;
    this.closeOverlay();
  }

  private toggleOverlay(target: HTMLElement | null): void {
    if (this.isOpen()) {
      this.closeOverlay();
      return;
    }
    this.openOverlay(target);
  }

  private openOverlay(target: HTMLElement | null): void {
    if (!target) return;
    this.triggerElement = target;
    const rect = target.getBoundingClientRect();
    this.lastTriggerRect = rect;
    this.position.set({
      top: rect.bottom + this.triggerOffset(),
      left: rect.left,
      width: rect.width,
    });
    this.isOpen.set(true);
    this.opened.emit();

    queueMicrotask(() => this.focusOverlay());

    setTimeout(() => {
      if (!this.isOpen() || !this.lastTriggerRect) return;
      this.recalculateOverlayPosition(this.lastTriggerRect);
    });
  }

  private recalculateOverlayPosition(triggerRect: DOMRect): void {
    const overlayRef = this.overlayElement?.nativeElement;
    if (!overlayRef) return;

    const overlayRect = overlayRef.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const availableBelow = viewportHeight - triggerRect.bottom;
    const availableAbove = triggerRect.top;
    const overlayTotalHeight = overlayRect.height + this.triggerOffset();

    let top = triggerRect.bottom + this.triggerOffset();
    if (availableBelow < overlayTotalHeight && availableAbove >= overlayTotalHeight) {
      top = triggerRect.top - overlayRect.height - this.triggerOffset();
    } else if (availableBelow < overlayTotalHeight) {
      const maxTop = Math.max(this.triggerOffset(), viewportHeight - overlayRect.height - this.triggerOffset());
      top = Math.min(top, maxTop);
    }

    const overlayTotalWidth = overlayRect.width;
    let left = triggerRect.left;
    if (left + overlayTotalWidth > viewportWidth - this.triggerOffset()) {
      left = Math.max(this.triggerOffset(), viewportWidth - overlayTotalWidth - this.triggerOffset());
    }
    if (left < this.triggerOffset()) {
      left = this.triggerOffset();
    }

    this.position.set({
      top,
      left,
      width: triggerRect.width,
    });
  }

  private setSelection(date: Date | null): void {
    this.selectedDate.set(date ? new Date(date) : null);
  }

  private emitValue(value: Date | null): void {
    this.onChange(value);
    this.valueChange.emit(value);
  }

  private closeOverlay(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.closed.emit(this.selectedDate());
    queueMicrotask(() => this.triggerElement?.focus());
  }

  private focusOverlay(): void {
    this.overlayElement?.nativeElement.querySelector<HTMLElement>('button, [tabindex]:not([tabindex="-1"])')?.focus();
  }

  private markAsTouched(): void {
    if (this.touched) return;
    this.onTouched();
    this.touched = true;
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    this.onClose();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.onClose();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.onClose();
  }
}
